import React from 'react';
import { Accordion, Badge, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { DEFAULT_TRACE_LABELS, TraceLabels } from './traceShapes';
import { TraceValueView } from './TraceValueView';
import type { NodeTraceEntry } from '../types/editor';
import classes from './NodeOutputsPanel.module.css';

export type NodeOutputsPanelProps = {
  nodeTraces: Record<string, NodeTraceEntry>;
  onNavigateNode?: ( nodeId: string ) => void;
  highlightedNodeId?: string | null;
  /** Translated UI labels. Defaults to hardcoded Spanish when omitted. */
  traceLabels?: TraceLabels;
};

/**
 * Format duration in milliseconds to a short display string.
 */
const formatDuration = ( ms: number ): string => {
  if ( !Number.isFinite( ms ) || ms < 0 ) return '';
  if ( ms < 1 ) return '<1 ms';
  if ( ms < 1000 ) return `${Math.round( ms )} ms`;
  return `${( ms / 1000 ).toFixed( 2 )} s`;
};

type NodeEntryViewProps = {
  entry: NodeTraceEntry;
  labels: TraceLabels;
};

/**
 * Renders the body of a single node entry: output/input/error + optional
 * nested iterations for iterator nodes (ForEach, Reduce).
 */
const NodeEntryBody: React.FC<NodeEntryViewProps> = ({ entry, labels }) => {
  const iterationEntries = entry.iterations
    ? Object.entries( entry.iterations ).sort(
      ([a], [b]) => Number( a ) - Number( b )
    )
    : [];

  return (
    <Stack gap="sm">
      {entry.status === 'error' ? (
        <>
          {entry.error !== undefined && (
            <Stack gap={4}>
              <Text className={classes.sectionLabel}>
                {labels.errorMessagePrefix ?? DEFAULT_TRACE_LABELS.errorMessagePrefix ?? 'Mensaje de error'}
              </Text>
              <div className={classes.errorMessage}>{String( entry.error )}</div>
            </Stack>
          )}
        </>
      ) : (
        entry.output !== undefined && (
          <Stack gap={4}>
            <Text className={classes.sectionLabel}>output</Text>
            <TraceValueView value={entry.output} traceLabels={labels} />
          </Stack>
        )
      )}

      {iterationEntries.length > 0 && (
        <Stack gap={4}>
          <Text className={classes.sectionLabel}>iterations</Text>
          <Accordion
            variant="contained"
            radius="xs"
            multiple
            className={classes.iterationsAccordion}
          >
            {iterationEntries.map(([ index, iterTraces ]) => (
              <Accordion.Item key={index} value={`iter-${index}`}>
                <Accordion.Control>
                  <Group gap="xs" wrap="nowrap">
                    <Badge size="xs" variant="light" color="grape">
                      #{index}
                    </Badge>
                    <Text size="10px" c="dimmed">
                      {iterTraces.length} {iterTraces.length === 1 ? 'node' : 'nodes'}
                    </Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {iterTraces.map(( sub ) => (
                      <Stack key={sub.nodeId} gap={4}>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="xs" className={classes.nodeId}>
                            {sub.nodeId}
                          </Text>
                          <Text size="10px" className={classes.nodeType}>
                            {sub.nodeType}
                          </Text>
                          <Badge
                            size="xs"
                            variant="light"
                            color={sub.status === 'error' ? 'red' : 'green'}
                          >
                            {sub.status}
                          </Badge>
                          {sub.duration !== undefined && (
                            <Text size="10px" className={classes.nodeDuration}>
                              {formatDuration( sub.duration )}
                            </Text>
                          )}
                        </Group>
                        <NodeEntryBody entry={sub} labels={labels} />
                      </Stack>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Stack>
      )}
    </Stack>
  );
};

/**
 * Side panel listing each executed node's output, grouped by node id.
 *
 * Replaces the previous `RefsPanel` which rendered `_refs` table entries.
 * The currently "active" node (e.g. the failed node after clicking "Jump to
 * failed node") is opened and scrolled into view via `highlightedNodeId`.
 */
export const NodeOutputsPanel: React.FC<NodeOutputsPanelProps> = ({
  nodeTraces,
  onNavigateNode,
  highlightedNodeId,
  traceLabels
}) => {
  const labels = traceLabels ?? DEFAULT_TRACE_LABELS;
  const itemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const [ openValue, setOpenValue ] = React.useState<string | null>( null );

  React.useEffect(() => {
    if ( !highlightedNodeId ) return;
    setOpenValue( highlightedNodeId );
    const el = itemRefs.current[highlightedNodeId];
    if ( el ) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [highlightedNodeId]);

  const entries = Object.entries( nodeTraces );

  if ( entries.length === 0 ) {
    const emptyLabel = traceLabels?.noNodesExecuted
      ?? DEFAULT_TRACE_LABELS.noNodesExecuted
      ?? 'Sin nodos ejecutados';
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          <Text size="xs" c="dimmed">
            {emptyLabel}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Group justify="space-between" className={classes.header}>
        <Text size="xs" fw={700} tt="uppercase" className={classes.title}>
          Nodes
        </Text>
        <Badge size="xs" variant="light" color="indigo">
          {entries.length}
        </Badge>
      </Group>
      <ScrollArea className={classes.scroll} scrollbarSize={6}>
        <Accordion
          variant="contained"
          radius="xs"
          value={openValue}
          onChange={setOpenValue}
        >
          {entries.map(([ nodeId, entry ]) => {
            const isError = entry.status === 'error';
            const itemClassName = [
              highlightedNodeId === nodeId ? classes.highlightedItem : null,
              isError ? classes.nodeError : classes.nodeSuccess
            ].filter( Boolean ).join( ' ' ) || undefined;

            return (
              <Accordion.Item
                key={nodeId}
                value={nodeId}
                ref={( el ) => { itemRefs.current[nodeId] = el; }}
                className={itemClassName}
              >
                <Accordion.Control
                  onClick={() => onNavigateNode?.( nodeId )}
                >
                  <Group gap="xs" wrap="nowrap">
                    <Text size="xs" className={classes.nodeId}>
                      {nodeId}
                    </Text>
                    <Text size="10px" className={classes.nodeType}>
                      {entry.nodeType}
                    </Text>
                    <Badge
                      size="xs"
                      variant="light"
                      color={isError ? 'red' : 'green'}
                    >
                      {entry.status}
                    </Badge>
                    {entry.duration !== undefined && (
                      <Text size="10px" className={classes.nodeDuration}>
                        {formatDuration( entry.duration )}
                      </Text>
                    )}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <NodeEntryBody entry={entry} labels={labels} />
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </ScrollArea>
    </div>
  );
};
